import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GasButton } from './GasButton';
import { translate } from '../hooks/useGasI18n';
import './gas-video-transcript.css';

/**
 * GasVideoTranscript — transcription interactive synchronisée (drawer).
 *
 * Affiche la transcription en lignes cliquables (clic → seek), surligne la ligne
 * active et la fait défiler ; si des mots timés sont fournis (`words`, issus du
 * STT via gas/v1/video/transcript), surligne aussi le **mot courant** (karaoké).
 *
 * Bloc e-learning prêt : présentation pure, piloté par `currentTime` + `onSeek`
 * (l'hôte branche `onTimeUpdate` du player → currentTime, et `onSeek` → seek).
 * NON exporté par l'index du kit (pas de dépendance lourde) — importer directement.
 */

export interface GasTranscriptCue {
    start: number;
    end: number;
    text: string;
}

export interface GasTranscriptWord {
    text: string;
    start: number;
    end: number;
}

/** Index de la cue active à l'instant t (start ≤ t < end ; sinon dernière cue commencée). Pure. */
export function activeCueIndex(cues: GasTranscriptCue[], currentTime: number): number {
    let active = -1;
    for (let i = 0; i < cues.length; i++) {
        const cue = cues[i];
        if (currentTime >= cue.start && currentTime < cue.end) {
            return i;
        }
        if (currentTime >= cue.start) {
            active = i;
        }
    }
    return active;
}

/** Index du mot actif à l'instant t (start ≤ t < end). -1 si aucun. Pure. */
export function activeWordIndex(words: GasTranscriptWord[], currentTime: number): number {
    for (let i = 0; i < words.length; i++) {
        if (currentTime >= words[i].start && currentTime < words[i].end) {
            return i;
        }
    }
    return -1;
}

/** Normalise pour une recherche insensible à la casse et aux accents. Pure. */
export function normalizeForSearch(value: string): string {
    return value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/**
 * Indices des cues dont le texte contient la requête (recherche dans la vidéo).
 * Insensible à la casse et aux accents. Requête vide → aucun résultat. Pure.
 */
export function searchCues(cues: GasTranscriptCue[], query: string): number[] {
    const needle = normalizeForSearch(query.trim());
    if (needle.length === 0) {
        return [];
    }
    const matches: number[] = [];
    cues.forEach((cue, index) => {
        if (normalizeForSearch(cue.text).includes(needle)) {
            matches.push(index);
        }
    });
    return matches;
}

export interface GasVideoTranscriptProps {
    cues: GasTranscriptCue[];
    words?: GasTranscriptWord[];
    currentTime: number;
    onSeek: (seconds: number) => void;
    title?: string;
    className?: string;
}

export function GasVideoTranscript({
    cues,
    words = [],
    currentTime,
    onSeek,
    title,
    className = '',
}: GasVideoTranscriptProps) {
    const activeCue = useMemo(() => activeCueIndex(cues, currentTime), [cues, currentTime]);
    const activeWord = useMemo(
        () => (words.length > 0 ? activeWordIndex(words, currentTime) : -1),
        [words, currentTime],
    );
    const listRef = useRef<HTMLDivElement>(null);
    const activeRef = useRef<HTMLButtonElement>(null);
    const matchRef = useRef<HTMLButtonElement>(null);
    const lastScrolledRef = useRef(-1);

    // Recherche dans la vidéo.
    const [query, setQuery] = useState('');
    const [matchPos, setMatchPos] = useState(0);
    const matches = useMemo(() => searchCues(cues, query), [cues, query]);
    const matchSet = useMemo(() => new Set(matches), [matches]);
    const currentMatchIndex = matches.length > 0 ? matches[matchPos] : -1;

    useEffect(() => {
        setMatchPos(0);
    }, [query]);

    function gotoMatch(pos: number) {
        if (matches.length === 0) {
            return;
        }
        const next = (pos + matches.length) % matches.length;
        setMatchPos(next);
        onSeek(cues[matches[next]].start);
    }

    // Scroll INTERNE au drawer (jamais la page) : on borne le scroll au conteneur
    // de liste pour que `scrollIntoView` ne fasse pas remonter la fenêtre.
    function scrollCueIntoView(el: HTMLButtonElement | null) {
        const list = listRef.current;
        if (!el || !list) {
            return;
        }
        const top = el.offsetTop - list.offsetTop;
        list.scrollTo({ top: top - list.clientHeight / 2 + el.clientHeight / 2, behavior: 'smooth' });
    }

    // Auto-scroll de la ligne en lecture (sans voler le focus / le scroll de page).
    useEffect(() => {
        if (activeCue >= 0 && activeCue !== lastScrolledRef.current) {
            lastScrolledRef.current = activeCue;
            scrollCueIntoView(activeRef.current);
        }
    }, [activeCue]);

    // Révèle le résultat de recherche courant dans le drawer (et ses mots).
    useEffect(() => {
        if (currentMatchIndex >= 0) {
            scrollCueIntoView(matchRef.current);
        }
    }, [currentMatchIndex]);

    if (cues.length === 0) {
        return null;
    }

    const hasQuery = query.trim().length > 0;

    return (
        <aside className={`gas-video-transcript ${className}`} aria-label={title || translate('video.transcript.title')}>
            {title ? <h3 className="gas-video-transcript__title">{title}</h3> : null}

            <div className="gas-video-transcript__search">
                <input
                    type="search"
                    className="gas-video-transcript__search-input"
                    placeholder={translate('video.transcript.search')}
                    aria-label={translate('video.transcript.search')}
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            gotoMatch(event.shiftKey ? matchPos - 1 : matchPos + 1);
                        }
                    }}
                />
                {hasQuery ? (
                    <span className="gas-video-transcript__search-nav">
                        <span className="gas-video-transcript__search-count" aria-live="polite">
                            {matches.length > 0 ? `${matchPos + 1}/${matches.length}` : '0'}
                        </span>
                        <GasButton
                            variant="ghost"
                            size="xs"
                            className="gas-video-transcript__search-btn"
                            aria-label={translate('video.transcript.prev')}
                            title={translate('video.transcript.prev')}
                            disabled={matches.length === 0}
                            onClick={() => gotoMatch(matchPos - 1)}
                        >‹</GasButton>
                        <GasButton
                            variant="ghost"
                            size="xs"
                            className="gas-video-transcript__search-btn"
                            aria-label={translate('video.transcript.next')}
                            title={translate('video.transcript.next')}
                            disabled={matches.length === 0}
                            onClick={() => gotoMatch(matchPos + 1)}
                        >›</GasButton>
                    </span>
                ) : null}
            </div>

            <div className="gas-video-transcript__list" ref={listRef}>
                {cues.map((cue, index) => {
                    const isActive = index === activeCue;
                    const isMatch = matchSet.has(index);
                    const isCurrentMatch = index === currentMatchIndex;
                    const ref = isCurrentMatch ? matchRef : isActive ? activeRef : undefined;
                    // On affiche les mots (karaoké) pour la ligne en lecture ET pour
                    // le résultat de recherche courant, afin de révéler ses mots.
                    const showWords = words.length > 0 && (isActive || isCurrentMatch);
                    return (
                        <button
                            key={`${cue.start}-${index}`}
                            ref={ref}
                            type="button"
                            className={[
                                'gas-video-transcript__cue',
                                isActive ? 'gas-video-transcript__cue--active' : '',
                                isMatch ? 'gas-video-transcript__cue--match' : '',
                                isCurrentMatch ? 'gas-video-transcript__cue--match-current' : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => onSeek(cue.start)}
                        >
                            {showWords
                                ? renderWordsForCue(cue, words, isActive ? activeWord : -1, hasQuery ? query : '')
                                : cue.text}
                        </button>
                    );
                })}
            </div>
        </aside>
    );
}

/**
 * Rend le texte de la cue mot-à-mot, en surlignant le mot en lecture (`activeWord`)
 * et, si une requête est fournie, les mots qui correspondent à la recherche.
 * Les mots de la cue = ceux dont le start tombe dans [cue.start, cue.end[.
 */
function renderWordsForCue(cue: GasTranscriptCue, words: GasTranscriptWord[], activeWord: number, query = '') {
    const inCue = words
        .map((word, globalIndex) => ({ word, globalIndex }))
        .filter(({ word }) => word.start >= cue.start && word.start < cue.end);

    if (inCue.length === 0) {
        return cue.text;
    }

    const needle = normalizeForSearch(query.trim());

    return inCue.map(({ word, globalIndex }) => {
        const isActive = globalIndex === activeWord;
        const isHit = needle.length > 0 && normalizeForSearch(word.text).includes(needle);
        return (
            <span
                key={`${globalIndex}-${word.start}`}
                className={[
                    'gas-video-transcript__word',
                    isActive ? 'gas-video-transcript__word--active' : '',
                    isHit ? 'gas-video-transcript__word--hit' : '',
                ].filter(Boolean).join(' ')}
            >
                {word.text}{' '}
            </span>
        );
    });
}
